import React, { useState } from 'react';
import Modal from 'react-modal';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';

// Import logos from assets
import salesforceLogo from '../assets/images/Salesforce_Logo.png';
import kaggleLogo from '../assets/images/kaggle.png'
import hubspotLogo from '../assets/images/hubspot.png';
import forageLogo from '../assets/images/forage.jpg'

const achievements = [
  {
    title: 'Platform Developer I',
    issuer: 'Salesforce',
    issuerLogo: salesforceLogo,
    date: 'June 2025',
    description: 'Validated expertise in designing custom business logic and interfaces using Apex, triggers, and Lightning Web Components.',
    certificateUrl: 'https://trailhead.salesforce.com/en/credentials/verification/'
  },
  {
    title: 'Agentforce Specialist',
    issuer: 'Salesforce',
    issuerLogo: salesforceLogo,
    date: 'December 2025',
    description: 'Demonstrated mastery in deploying autonomous AI agents, configuring trust parameters, and coding prompt templates.',
    certificateUrl: 'https://trailhead.salesforce.com/en/credentials/verification/'
  },
  {
    title: 'Double Star Ranger',
    issuer: 'Salesforce Trailhead',
    issuerLogo: salesforceLogo,
    date: 'Active',
    description: 'Reached 200,000+ points and completed 200+ badges covering cloud engineering, database security, and declarative automation.',
    certificateUrl: 'https://www.salesforce.com/trailblazer/chahatjain'
  },
  {
    title: 'Google GenAI Capstone Specialist',
    issuer: 'Google x Kaggle',
    issuerLogo: kaggleLogo,
    date: 'July 2025',
    description: 'Built a personalized career mentor AI utilizing Gemini APIs, grounding, NLP parsing, and document understanding.',
    certificateUrl: 'https://www.kaggle.com/code/chahatj23/personalized-ai-mentor'
  },
  {
    title: 'Kaggle Capstone Award',
    issuer: 'Kaggle',
    issuerLogo: kaggleLogo,
    date: 'July 2025',
    description: 'Awarded top-tier classification for NLP models and GenAI workflow implementations.',
    certificateUrl: 'https://www.kaggle.com/chahatj23'
  },
  {
    title: 'Hubspot Reporting Certified',
    issuer: 'Hubspot',
    issuerLogo: hubspotLogo,
    date: 'December 2025',
    description: 'Validated skills in database report pipelines, conversion funnel tracking, and marketing dashboards.',
    certificateUrl: 'https://app.hubspot.com/academy/achievements/wmgbqc4p/en/1/chahat-jain/hubspot-reporting'
  },
  {
    title: 'Hubspot Marketing Hub Software',
    issuer: 'Hubspot',
    issuerLogo: hubspotLogo,
    date: 'August 2025',
    description: 'Mastered marketing pipeline setups, automated campaigns, and email sync integrations.',
    certificateUrl: 'https://app.hubspot.com/academy/achievements/g366bvdj/en/1/chahat-jain/hubspot-marketing-hub-software'
  },
  {
    title: 'Hubspot Service Hub Software',
    issuer: 'Hubspot',
    issuerLogo: hubspotLogo,
    date: 'August 2025',
    description: 'Validated customer service workflows, automated ticketing sync, and customer feedback pipelines.',
    certificateUrl: 'https://app.hubspot.com/academy/achievements/w0nrwcms/en/1/chahat-jain/service-hub-software'
  },
  {
    title: 'Hubspot Sales Hub Software',
    issuer: 'Hubspot',
    issuerLogo: hubspotLogo,
    date: 'August 2025',
    description: 'Mastered sales pipeline design, deal progression rules, and contact synchronization flows.',
    certificateUrl: 'https://app.hubspot.com/academy/achievements/w1yzs1ps/en/1/chahat-jain/hubspot-sales-hub-software'
  },
  {
    title: 'Software Engineering Virtual Experience',
    issuer: 'J.P. Morgan',
    issuerLogo: forageLogo,
    date: 'August 2025',
    description: 'Participated in code modularization, dashboard chart displays, and repository audits.',
    certificateUrl: 'https://forage-uploads-prod.s3.amazonaws.com/completion-certificates/J.P.%20Morgan/R5iK7HMxJGBgaSbvk_J.P.%20Morgan_dxeP4jNqH9P7u4MGh_1724741587280_completion_certificate.pdf'
  }
];

Modal.setAppElement('#root'); // set to root div

const Certifications = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);

  const handleCertClick = (cert) => {
    // If it's an external verification url, open in new tab
    if (cert.certificateUrl.startsWith('http')) {
      window.open(cert.certificateUrl, '_blank', 'noopener,noreferrer');
    } else {
      setSelectedCert(cert);
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCert(null);
  };

  return (
    <section id="achievements" className="px-4 md:px-10 py-24 bg-transparent text-white max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <p className="text-xs tracking-[0.35em] text-white/60 uppercase">
          Credentials
        </p>
        <h2 className="mt-3 text-4xl md:text-5xl font-bold leading-tight">
          Verified <span className="bg-gradient-to-r from-cyan-400 to-indigo-400 bg-clip-text text-transparent">Certifications</span>
        </h2>
        <p className="text-gray-400 mt-4 max-w-xl mx-auto text-sm md:text-base">
          Click on any credential card to verify status online via Trailhead, Kaggle, HubSpot, or Forage verification platforms.
        </p>
      </div>


      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {achievements.map((item, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: idx * 0.05 }}
            className="bg-white/5 border border-white/10 rounded-2xl p-6 shadow-xl backdrop-blur-md hover:border-cyan-400/50 hover:shadow-cyan-500/10 transition duration-300 cursor-pointer flex flex-col justify-between"
            onClick={() => handleCertClick(item)}
          >
            <div>
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
                <h3 className="text-md font-bold text-white tracking-wide">{item.title}</h3>
                <img src={item.issuerLogo} alt={item.issuer} className="h-7 w-7 rounded-lg object-contain bg-white/5 p-0.5" />
              </div>
              <p className="text-cyan-300 text-xs font-semibold mb-2">{item.issuer} • {item.date}</p>
              <p className="text-xs text-gray-400 leading-relaxed font-medium">{item.description}</p>
            </div>
            
            <div className="mt-6 flex items-center justify-end text-cyan-300 hover:text-cyan-400 text-xs font-bold gap-1">
              <span>Verify Credential</span>
              <ExternalLink size={12} />
            </div>
          </motion.div>
        ))}
      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        className="bg-[#0b1120] max-w-3xl w-full p-6 rounded-2xl border border-cyan-500 mx-auto my-20 outline-none shadow-2xl"
        overlayClassName="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 backdrop-blur-md"
      >
        {selectedCert && (
          <>
            <h3 className="text-xl text-white font-bold mb-4">{selectedCert.title}</h3>
            <iframe
              src={selectedCert.certificateUrl}
              className="w-full h-[400px] rounded-xl border border-white/10"
              title="Certificate Viewer"
            />
            <div className="mt-4 flex justify-end gap-3">
              <a
                href={selectedCert.certificateUrl}
                download
                className="bg-cyan-600 text-white text-xs font-semibold px-4 py-2.5 rounded-xl hover:bg-cyan-700 transition"
              >
                Download PDF
              </a>
              <button
                onClick={closeModal}
                className="bg-slate-800 text-white text-xs font-semibold px-4 py-2.5 rounded-xl hover:bg-slate-700 transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </>
        )}
      </Modal>
    </section>
  );
};

export default Certifications;
